import { type Problem, problemSchema } from '@thrivehrm/shared';
import type { z } from 'zod';

/**
 * Typed fetch wrapper for the ThriveHRM API (`/api/v1`, same origin — Vite proxies it in dev).
 * - Sends the session cookie; adds `X-CSRF-Token` (double-submit cookie) on mutating requests.
 * - 401 → calls the unauthorized handler (redirect to /login).
 * - Errors are RFC 9457 problem+json → thrown as `ApiError` with the parsed problem.
 */

export const API_BASE = '/api/v1';
export const CSRF_COOKIE = 'thrivehrm_csrf';
export const CSRF_HEADER = 'X-CSRF-Token';

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem;

  constructor(problem: Problem) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
    this.status = problem.status;
    this.problem = problem;
  }

  /** Field errors keyed by dotted path, for react-hook-form `setError`. */
  get fieldErrors(): Record<string, string> {
    return Object.fromEntries((this.problem.errors ?? []).map((e) => [e.path, e.message]));
  }
}

type UnauthorizedHandler = () => void;

let onUnauthorized: UnauthorizedHandler = () => {
  const { pathname, search } = window.location;
  if (pathname === '/login') return;
  const next = encodeURIComponent(`${pathname}${search}`);
  window.location.assign(`/login?next=${next}`);
};

/** Replaces the 401 handler (the router installs a client-side redirect; tests use a spy). */
export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  onUnauthorized = handler;
}

export function readCookie(name: string): string | undefined {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const cookie = part.trim();
    if (cookie.startsWith(prefix)) return decodeURIComponent(cookie.slice(prefix.length));
  }
  return undefined;
}

/**
 * Double-submit CSRF token (FR-AUTH-6): the API sets it at sign-in; before that (dev login) the
 * SPA creates one itself. The API only checks that header and cookie match, and a cross-site
 * page can neither read nor set this cookie.
 */
export function ensureCsrfToken(): string {
  const existing = readCookie(CSRF_COOKIE);
  if (existing) return existing;
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const token = btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${CSRF_COOKIE}=${token}; Path=/; SameSite=Lax${secure}`;
  return token;
}

export interface RequestOptions<T> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | readonly string[] | undefined>;
  /** Validates the response body (shared Zod schema). */
  schema?: z.ZodType<T>;
  signal?: AbortSignal;
  /** Don't run the 401 handler (e.g. the session probe on start-up). */
  ignoreUnauthorized?: boolean;
}

/** Full URL of an API path with a query (arrays as repeated keys), e.g. for download links. */
export function apiUrl(path: string, query: RequestOptions<unknown>['query']): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined) continue;
    if (Array.isArray(value)) for (const v of value as readonly string[]) params.append(key, v);
    else params.append(key, String(value));
  }
  const qs = params.toString();
  return `${API_BASE}${path}${qs ? `?${qs}` : ''}`;
}

async function toProblem(res: Response): Promise<Problem> {
  try {
    const parsed = problemSchema.safeParse(await res.json());
    if (parsed.success) return parsed.data;
  } catch {
    // Not JSON (e.g. a proxy error page).
  }
  return { type: 'about:blank', title: res.statusText || 'Error', status: res.status };
}

export async function apiRequest<T = unknown>(
  path: string,
  options: RequestOptions<T> = {},
): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };
  // FormData (file uploads) sets its own multipart boundary; everything else is JSON.
  const multipart = options.body instanceof FormData;
  if (options.body !== undefined && !multipart) headers['Content-Type'] = 'application/json';
  if (MUTATING.has(method)) headers[CSRF_HEADER] = ensureCsrfToken();

  let res: Response;
  try {
    res = await fetch(apiUrl(path, options.query), {
      method,
      headers,
      credentials: 'same-origin',
      ...(options.body === undefined
        ? {}
        : { body: multipart ? (options.body as FormData) : JSON.stringify(options.body) }),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error: unknown) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError({
      type: 'about:blank',
      title: 'Network error',
      status: 0,
      detail: 'Could not reach ThriveHRM. Check your connection and try again.',
    });
  }

  if (res.status === 401 && !options.ignoreUnauthorized) onUnauthorized();
  if (!res.ok) throw new ApiError(await toProblem(res));
  if (res.status === 204) return undefined as T;

  const data: unknown = await res.json();
  return options.schema ? options.schema.parse(data) : (data as T);
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions<T>, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions<T>, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions<T>, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions<T>, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions<T>, 'method'>) =>
    apiRequest<T>(path, { ...options, method: 'DELETE' }),
};
